/**
 * The gap list.
 *
 * The refusals from every questionnaire, grouped and counted. This is the screen
 * that turns "we have sixty unanswered questions" into "write these four cards
 * and most of them go away", and it is why refusing is worth more than guessing:
 * a fabricated answer produces nothing to work from.
 */

import { useEffect, useState } from 'react';
import { api, type Gap } from '../api';
import { link } from '../router';
import { Button, EmptyState, PageHeader, Spinner } from '../ui';
import { CircleCheckIcon, Icon, IconHover, PlusIcon } from '../icons';

export function GapsView() {
  const [gaps, setGaps] = useState<Gap[] | null>(null);

  useEffect(() => {
    void api
      .gaps()
      .then(({ gaps }) => setGaps(gaps))
      .catch(() => setGaps([]));
  }, []);

  return (
    <>
      <PageHeader
        title="Gaps"
        description="Questions your evidence does not answer yet, ordered by how often customers ask them. Write the evidence at the top of this list first."
        actions={
          <a {...link('/evidence')}>
            <Button variant="primary" icon={PlusIcon}>
              Write evidence
            </Button>
          </a>
        }
      />

      {gaps === null ? (
        <div className="flex justify-center py-16">
          <Spinner size={20} />
        </div>
      ) : gaps.length === 0 ? (
        <EmptyState icon={CircleCheckIcon} title="No gaps">
          Every question you have drafted so far is backed by evidence. When something is refused, it
          appears here with the customers who asked it.
        </EmptyState>
      ) : (
        <div className="space-y-2.5">
          {gaps.map((gap) => (
            <IconHover key={gap.key} className="card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="min-w-0 flex-1 text-[14px] leading-relaxed">{gap.text}</p>
                <span className={`chip ${gap.count > 1 ? 'chip-warn' : 'chip-neutral'}`}>
                  asked {gap.count}×
                </span>
              </div>

              <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[12.5px] text-faint">
                {gap.askedBy.map((customer) => (
                  <span key={customer} className="chip chip-neutral">
                    {customer}
                  </span>
                ))}
                {gap.reasons.includes('stale_evidence') ? (
                  <span className="chip chip-refusal">Evidence exists but is stale</span>
                ) : null}
              </div>
            </IconHover>
          ))}

          <div className="flex items-center justify-center gap-1.5 pt-4 text-center text-[12.5px] text-faint">
            <Icon icon={CircleCheckIcon} size={12} isAnimated={false} />
            {gaps.length} distinct {gaps.length === 1 ? 'gap' : 'gaps'} across your questionnaires.
          </div>
        </div>
      )}
    </>
  );
}
